export type TypeRunWithConcurrencyUploadOutcome = 'ok' | 'error' | 'skipped' | 'aborted';

export type TypeRunWithConcurrencyUploadOptions<T> = {
    shouldStop?: () => boolean;
    stopOnError?: boolean;
    throwOnError?: boolean;

    onError?: (err: unknown, item: T, index: number) => void;
    onOutcome?: (outcome: TypeRunWithConcurrencyUploadOutcome, item: T, index: number) => void;
};

export type TypeRunWithConcurrencyUploadResult<T> = {
    okCount: number;
    errorCount: number;
    skippedCount: number;
    abortedCount: number;
    errors: { index: number; item: T; error: unknown }[];
};

// ----------------------------------------------------------------------------

async function runWithConcurrencyUpload<T>(
    items: T[],
    concurrency: number,
    worker: (item: T, index: number) => Promise<TypeRunWithConcurrencyUploadOutcome>,
    options?: TypeRunWithConcurrencyUploadOptions<T>,
): Promise<TypeRunWithConcurrencyUploadResult<T>> {
    /*
     * A non-numeric concurrency would leave `limit` as NaN, which `Array.from` reads as zero
     * runners: no file would be sent and the run would report every one as never attempted.
     */
    const requested = Math.floor(Number(concurrency));
    const safeConcurrency = Number.isFinite(requested) ? Math.max(1, requested) : 1;
    const limit = Math.min(safeConcurrency, items.length);

    const shouldStop = options?.shouldStop;
    const stopOnError = options?.stopOnError === true;
    const throwOnError = options?.throwOnError === true;

    let nextIndex = 0;
    let stop = false;

    let okCount = 0;
    let errorCount = 0;
    let skippedCount = 0;
    let abortedCount = 0;

    const errors: TypeRunWithConcurrencyUploadResult<T>['errors'] = [];

    const runners = Array.from({ length: limit }, async (): Promise<any> => {
        while (true) {
            if (stop) {
                return;
            }

            if (shouldStop?.()) {
                stop = true;
                return;
            }

            const currentIndex = nextIndex;
            nextIndex += 1;

            if (currentIndex >= items.length) {
                return;
            }

            const item = items[currentIndex];

            // One more check after reserving the index: helps stop sooner.
            if (stop) {
                return;
            }
            if (shouldStop?.()) {
                stop = true;
                return;
            }

            try {
                const outcome = await worker(item, currentIndex);

                // Normalize outcome to keep logic robust.
                const normalized: TypeRunWithConcurrencyUploadOutcome =
                    outcome === 'ok' || outcome === 'error' || outcome === 'skipped' || outcome === 'aborted'
                        ? outcome
                        : 'error';

                // Update counts
                if (normalized === 'ok') {
                    okCount += 1;
                } else if (normalized === 'skipped') {
                    skippedCount += 1;
                } else if (normalized === 'aborted') {
                    abortedCount += 1;
                } else {
                    errorCount += 1;

                    if (stopOnError) {
                        stop = true;
                    }
                }

                try {
                    options?.onOutcome?.(normalized, item, currentIndex);
                } catch (handlerErr) {
                    // Treat handler crash as an error (but don't double-count item error)
                    errors.push({ index: currentIndex, item, error: handlerErr });
                    try {
                        options?.onError?.(handlerErr, item, currentIndex);
                    } catch (handlerErr2) {
                        errors.push({ index: currentIndex, item, error: handlerErr2 });
                    }
                    if (stopOnError) {
                        stop = true;
                    }
                }

                if (stop) {
                    return;
                }
            } catch (err) {
                errors.push({ index: currentIndex, item, error: err });

                try {
                    options?.onError?.(err, item, currentIndex);
                } catch (handlerErr) {
                    errors.push({ index: currentIndex, item, error: handlerErr });
                }

                errorCount += 1;

                if (stopOnError) {
                    stop = true;
                    return;
                }
            }
        }
    });

    await Promise.all(runners);

    const result: TypeRunWithConcurrencyUploadResult<T> = {
        okCount,
        errorCount,
        skippedCount,
        abortedCount,
        errors,
    };

    if (throwOnError && result.errorCount > 0) {
        const err = result.errors[0]?.error;
        throw err instanceof Error ? err : new Error('Unknown error');
    }

    return result;
}

// ----------------------------------------------------------------------------

export default runWithConcurrencyUpload;
